import Vue from 'vue'
import VueRouter from 'vue-router'
import Layout from '@/layout/index.vue'

Vue.use(VueRouter)

// Vue Router 3 在导航到当前路由时会抛出 NavigationDuplicated，全局静默处理
// 路由守卫使用 next({ ...to, replace: true }) 时会产生 NavigationRedirected；
// 重复点击同一菜单会产生 NavigationDuplicated；两者均为预期行为，静默处理。
const SILENT_ERRORS = new Set(['NavigationDuplicated', 'NavigationRedirected'])

const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject)
  }
  return originalPush.call(this, location).catch(err => {
    if (err && SILENT_ERRORS.has(err.name)) return err
    return Promise.reject(err)
  })
}

const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalReplace.call(this, location, onResolve, onReject)
  }
  return originalReplace.call(this, location).catch(err => {
    if (err && SILENT_ERRORS.has(err.name)) return err
    return Promise.reject(err)
  })
}

/**
 * 常量路由（不需要权限，所有人可访问）
 *
 * 架构说明：所有实际页面（包括隐藏的详情/编辑页）均作为根路由 '/' 的子路由，
 * 共享同一个 Layout 实例。动态权限路由也通过 router.addRoute('Root', ...) 
 * 的方式挂载为同一 Layout 的子路由。
 * 这样无论在任意页面间导航，Layout 永远不会被销毁，
 * AppMain 内的 keep-alive 缓存始终有效，彻底解决跨模块导航导致页面状态丢失的问题。
 */
export const constantRoutes = [
  {
    path: '/login',
    component: () => import('@/views/login/index.vue'),
    hidden: true
  },
  {
    path: '/404',
    component: () => import('@/views/error/404.vue'),
    hidden: true
  },
  {
    path: '/redirect',
    component: Layout,
    hidden: true,
    children: [
      {
        path: '/redirect/:path(.*)',
        component: () => import('@/views/redirect/index.vue')
      }
    ]
  },
  {
    path: '/',
    name: 'Root',
    component: Layout,
    redirect: '/dashboard',
    children: [
      // ── 固定标签页 ──────────────────────────────────────────────────
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/index.vue'),
        meta: { title: '首页', icon: 'el-icon-s-home', affix: true }
      },
      // ── 个人中心 ────────────────────────────────────────────────────
      {
        path: '/profile/index',
        name: 'Profile',
        hidden: true,
        component: () => import('@/views/profile/index.vue'),
        meta: { title: '个人中心' }
      },
      // ── 产品 ────────────────────────────────────────────────────────
      {
        path: '/product/edit',
        name: 'ProductEdit',
        hidden: true,
        component: () => import('@/views/product/edit.vue'),
        meta: { title: '产品编辑', activeMenu: '/product/index' }
      },
      {
        path: '/product/detail',
        name: 'ProductDetail',
        hidden: true,
        component: () => import('@/views/product/detail.vue'),
        meta: { title: '产品详情', activeMenu: '/product/index' }
      },
      // ── 优惠券 ──────────────────────────────────────────────────────
      {
        path: '/coupon/edit',
        name: 'CouponEdit',
        hidden: true,
        component: () => import('@/views/coupon/edit.vue'),
        meta: { title: '优惠券编辑', activeMenu: '/coupon/index' }
      },
      {
        path: '/coupon/detail',
        name: 'CouponDetail',
        hidden: true,
        component: () => import('@/views/coupon/detail.vue'),
        meta: { title: '优惠券详情', activeMenu: '/coupon/index' }
      },
      {
        path: '/coupon/issue',
        name: 'CouponIssue',
        hidden: true,
        component: () => import('@/views/coupon/issue.vue'),
        meta: { title: '发放优惠券', activeMenu: '/coupon/index' }
      },
      // ── 合伙人 ──────────────────────────────────────────────────────
      {
        path: '/partner/detail',
        name: 'PartnerDetail',
        hidden: true,
        component: () => import('@/views/partner/detail.vue'),
        meta: { title: '合伙人详情', activeMenu: '/partner/index' }
      },
      // ── 订单 ────────────────────────────────────────────────────────
      {
        path: '/order/product/detail',
        name: 'ProductOrderDetail',
        hidden: true,
        component: () => import('@/views/order/product/detail.vue'),
        meta: { title: '商品订单详情', activeMenu: '/order/product' }
      },
      {
        path: '/order/service/detail',
        name: 'ServiceOrderDetail',
        hidden: true,
        component: () => import('@/views/order/service/detail.vue'),
        meta: { title: '服务订单详情', activeMenu: '/order/service' }
      }
    ]
  }
]

const createRouter = () => new VueRouter({
  mode: 'history',
  scrollBehavior: () => ({ y: 0 }),
  routes: constantRoutes
})

const router = createRouter()

export function resetRouter() {
  const newRouter = createRouter()
  router.matcher = newRouter.matcher
}

export default router
